import { AfterViewInit, Component, ElementRef, HostListener, Inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@shared/pipes/translate.pipe';

export interface ImagePaintDialogData {
  /** Object URL de la imagen original. Sin él, se abre un lienzo en blanco. */
  imageUrl?: string;
  fileName: string;
  /** Tamaño del lienzo en blanco (solo si no hay imageUrl). */
  blankSize?: { width: number; height: number };
}

type PaintTool = 'brush' | 'line' | 'arrow' | 'circle' | 'rect' | 'text';

/**
 * Editor de pintura sobre una imagen (anotaciones estilo Paint):
 * pincel / línea / círculo / rectángulo, colores, grosor y deshacer.
 * Al guardar devuelve un Blob PNG con la imagen editada.
 */
@Component({
  selector: 'app-image-paint-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  template: `
    <div class="paint-wrap">
      <div class="paint-header">
        <span class="paint-name">{{ data.fileName }}</span>
        <button mat-icon-button mat-dialog-close><mat-icon>close</mat-icon></button>
      </div>

      <div class="paint-toolbar">
        <!-- Herramientas -->
        <div class="tool-group">
          @for (t of tools; track t.id) {
            <button class="tool-btn"
                    [class.active]="tool() === t.id"
                    [matTooltip]="t.labelKey | translate"
                    (click)="tool.set(t.id)">
              <mat-icon>{{ t.icon }}</mat-icon>
            </button>
          }
        </div>

        <span class="toolbar-divider"></span>

        <!-- Colores -->
        <div class="tool-group">
          @for (c of colors; track c) {
            <button class="color-swatch"
                    [class.active]="color() === c"
                    [style.background]="c"
                    (click)="color.set(c)">
            </button>
          }
        </div>

        <span class="toolbar-divider"></span>

        <!-- Grosor -->
        <div class="tool-group" [matTooltip]="'gdp.paint.size' | translate">
          @for (s of sizes; track s) {
            <button class="size-btn" [class.active]="size() === s" (click)="size.set(s)">
              <span class="size-dot" [style.width.px]="s + 3" [style.height.px]="s + 3" [style.background]="color()"></span>
            </button>
          }
        </div>

        <span class="toolbar-divider"></span>

        <button mat-icon-button
                [matTooltip]="'gdp.paint.undo' | translate"
                [disabled]="undoDepth() === 0"
                (click)="undo()">
          <mat-icon>undo</mat-icon>
        </button>
        <button mat-icon-button
                [matTooltip]="'gdp.paint.redo' | translate"
                [disabled]="redoDepth() === 0"
                (click)="redo()">
          <mat-icon>redo</mat-icon>
        </button>
      </div>

      <div class="canvas-scroll" #canvasScroll>
        <canvas #canvas
                class="paint-canvas"
                (pointerdown)="onPointerDown($event)"
                (pointermove)="onPointerMove($event)"
                (pointerup)="onPointerUp($event)"
                (pointerleave)="onPointerUp($event)">
        </canvas>
        <!-- Entrada de texto flotante (herramienta texto) -->
        @if (textEditing(); as te) {
          <input #textField
                 class="text-input"
                 type="text"
                 [style.left.px]="te.left"
                 [style.top.px]="te.top"
                 [style.color]="color()"
                 [style.fontSize.px]="textScreenPx()"
                 [(ngModel)]="textValue"
                 (keydown.enter)="commitText()"
                 (keydown.escape)="cancelText()"
                 (blur)="commitText()">
        }
      </div>

      <div class="paint-actions">
        <button mat-button mat-dialog-close>{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button color="primary" (click)="save()">
          <mat-icon>save</mat-icon>
          {{ 'common.save' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .paint-wrap {
      display: flex;
      flex-direction: column;
      max-width: 90vw;
      max-height: 90vh;
    }
    .paint-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 8px 8px 0 16px;
    }
    .paint-name {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--gdp-ink, #1b1464);
    }
    .paint-toolbar {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      padding: 8px 16px;
      border-bottom: 1px solid var(--gdp-border, #e6e8f0);
    }
    .tool-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .toolbar-divider {
      width: 1px;
      height: 24px;
      background: var(--gdp-border, #e6e8f0);
    }
    .tool-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      cursor: pointer;
      color: var(--gdp-muted, #6b7280);
    }
    .tool-btn.active {
      border-color: var(--brand-cyan, #29abe2);
      background: rgba(41, 171, 226, 0.12);
      color: var(--gdp-ink, #1b1464);
    }
    .color-swatch {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 0 0 1px var(--gdp-border, #ccc);
      cursor: pointer;
      padding: 0;
    }
    .color-swatch.active {
      box-shadow: 0 0 0 2px var(--gdp-ink, #1b1464);
    }
    .size-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      cursor: pointer;
    }
    .size-btn.active {
      border-color: var(--brand-cyan, #29abe2);
      background: rgba(41, 171, 226, 0.12);
    }
    .size-dot { border-radius: 50%; display: block; }
    .canvas-scroll {
      position: relative;
      overflow: auto;
      background:
        repeating-conic-gradient(#f0f0f3 0% 25%, white 0% 50%) 0 0 / 20px 20px;
      max-height: calc(90vh - 180px);
    }
    .paint-canvas {
      display: block;
      max-width: 86vw;
      height: auto;
      cursor: crosshair;
      touch-action: none;
    }
    .paint-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 10px 16px;
    }
    .text-input {
      position: absolute;
      background: rgba(255, 255, 255, 0.85);
      border: 1px dashed var(--brand-cyan, #29abe2);
      border-radius: 4px;
      outline: none;
      padding: 2px 6px;
      font-family: Montserrat, sans-serif;
      font-weight: 700;
      min-width: 120px;
    }
  `]
})
export class ImagePaintDialogComponent implements AfterViewInit {
  readonly tools: { id: PaintTool; icon: string; labelKey: string }[] = [
    { id: 'brush', icon: 'brush', labelKey: 'gdp.paint.brush' },
    { id: 'line', icon: 'timeline', labelKey: 'gdp.paint.line' },
    { id: 'arrow', icon: 'north_east', labelKey: 'gdp.paint.arrow' },
    { id: 'circle', icon: 'radio_button_unchecked', labelKey: 'gdp.paint.circle' },
    { id: 'rect', icon: 'crop_square', labelKey: 'gdp.paint.rect' },
    { id: 'text', icon: 'title', labelKey: 'gdp.paint.text' }
  ];
  readonly colors = ['#d64550', '#f59e0b', '#2e9e5b', '#29abe2', '#1b1464', '#111111', '#ffffff'];
  readonly sizes = [2, 5, 10];

  readonly tool = signal<PaintTool>('brush');
  readonly color = signal(this.colors[0]);
  readonly size = signal(5);
  readonly undoDepth = signal(0);
  readonly redoDepth = signal(0);

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly canvasScrollRef = viewChild.required<ElementRef<HTMLElement>>('canvasScroll');
  private readonly textFieldRef = viewChild<ElementRef<HTMLInputElement>>('textField');

  /** Estado de la herramienta texto: posición del input flotante + destino en el canvas. */
  readonly textEditing = signal<{ left: number; top: number; canvasX: number; canvasY: number } | null>(null);
  textValue = '';

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  private startX = 0;
  private startY = 0;
  /** Snapshot del lienzo al empezar cada trazo (para preview de formas y undo). */
  private strokeSnapshot: ImageData | null = null;
  private undoStack: ImageData[] = [];
  private redoStack: ImageData[] = [];
  private static readonly MAX_UNDO = 25;

  constructor(
    public dialogRef: MatDialogRef<ImagePaintDialogComponent, Blob | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ImagePaintDialogData
  ) {}

  ngAfterViewInit(): void {
    const canvas = this.canvasRef().nativeElement;
    if (!this.data.imageUrl) {
      // Modo "dibujar": lienzo en blanco
      const { width, height } = this.data.blankSize ?? { width: 1200, height: 800 };
      canvas.width = width;
      canvas.height = height;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.fillStyle = 'white';
      this.ctx.fillRect(0, 0, width, height);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      return;
    }
    const image = new Image();
    image.onload = () => {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.drawImage(image, 0, 0);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    };
    image.src = this.data.imageUrl;
  }

  /** Coordenadas del puntero en píxeles del canvas (compensa el escalado CSS). */
  private canvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }

  /** Tamaño de fuente en píxeles del canvas según el grosor elegido. */
  private textCanvasPx(): number {
    return { 2: 20, 5: 34, 10: 56 }[this.size()] ?? 34;
  }

  /** Tamaño de fuente del input flotante (en pantalla, compensando el escalado CSS). */
  textScreenPx(): number {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();
    return this.textCanvasPx() * (rect.width / canvas.width);
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.ctx) {
      return;
    }
    event.preventDefault();

    if (this.tool() === 'text') {
      // si había un texto a medias, confírmalo antes de abrir otro
      this.commitText();
      const scroll = this.canvasScrollRef().nativeElement;
      const scrollRect = scroll.getBoundingClientRect();
      const { x, y } = this.canvasPoint(event);
      this.textEditing.set({
        left: event.clientX - scrollRect.left + scroll.scrollLeft,
        top: event.clientY - scrollRect.top + scroll.scrollTop,
        canvasX: x,
        canvasY: y
      });
      this.textValue = '';
      setTimeout(() => this.textFieldRef()?.nativeElement.focus());
      return;
    }

    const canvas = this.canvasRef().nativeElement;
    this.strokeSnapshot = this.ctx.getImageData(0, 0, canvas.width, canvas.height);
    this.checkpoint(this.strokeSnapshot);

    const { x, y } = this.canvasPoint(event);
    this.startX = x;
    this.startY = y;
    this.drawing = true;

    this.ctx.strokeStyle = this.color();
    this.ctx.lineWidth = this.size();

    if (this.tool() === 'brush') {
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
      // un punto suelto también pinta
      this.ctx.lineTo(x + 0.01, y + 0.01);
      this.ctx.stroke();
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.drawing || !this.ctx) {
      return;
    }
    const { x, y } = this.canvasPoint(event);

    if (this.tool() === 'brush') {
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
      return;
    }

    // formas: restaurar el estado inicial y dibujar la preview
    if (this.strokeSnapshot) {
      this.ctx.putImageData(this.strokeSnapshot, 0, 0);
    }
    this.ctx.strokeStyle = this.color();
    this.ctx.lineWidth = this.size();
    this.drawShape(x, y);
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.drawing || !this.ctx) {
      return;
    }
    if (this.tool() !== 'brush') {
      const { x, y } = this.canvasPoint(event);
      if (this.strokeSnapshot) {
        this.ctx.putImageData(this.strokeSnapshot, 0, 0);
      }
      this.ctx.strokeStyle = this.color();
      this.ctx.lineWidth = this.size();
      this.drawShape(x, y);
    }
    this.drawing = false;
    this.strokeSnapshot = null;
  }

  private drawShape(x: number, y: number): void {
    this.ctx.beginPath();
    switch (this.tool()) {
      case 'line':
        this.ctx.moveTo(this.startX, this.startY);
        this.ctx.lineTo(x, y);
        break;
      case 'arrow': {
        this.ctx.moveTo(this.startX, this.startY);
        this.ctx.lineTo(x, y);
        // punta proporcional al grosor
        const angle = Math.atan2(y - this.startY, x - this.startX);
        const head = Math.max(12, this.size() * 4);
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(x - head * Math.cos(angle - Math.PI / 6), y - head * Math.sin(angle - Math.PI / 6));
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(x - head * Math.cos(angle + Math.PI / 6), y - head * Math.sin(angle + Math.PI / 6));
        break;
      }
      case 'circle': {
        const radius = Math.hypot(x - this.startX, y - this.startY);
        this.ctx.arc(this.startX, this.startY, radius, 0, Math.PI * 2);
        break;
      }
      case 'rect':
        this.ctx.rect(this.startX, this.startY, x - this.startX, y - this.startY);
        break;
      default:
        return;
    }
    this.ctx.stroke();
  }

  commitText(): void {
    const editing = this.textEditing();
    if (!editing) {
      return;
    }
    // Medir el input flotante ANTES de cerrarlo: el texto se pinta exactamente
    // donde el usuario lo estaba viendo (compensa padding/borde/line-height).
    const canvas = this.canvasRef().nativeElement;
    const canvasRect = canvas.getBoundingClientRect();
    let drawX = editing.canvasX;
    let drawY = editing.canvasY;
    const inputEl = this.textFieldRef()?.nativeElement;
    if (inputEl && canvasRect.width > 0) {
      const inputRect = inputEl.getBoundingClientRect();
      const styles = getComputedStyle(inputEl);
      const textLeft = inputRect.left + parseFloat(styles.borderLeftWidth) + parseFloat(styles.paddingLeft);
      const textMiddle = inputRect.top + inputRect.height / 2;
      drawX = (textLeft - canvasRect.left) * (canvas.width / canvasRect.width);
      drawY = (textMiddle - canvasRect.top) * (canvas.height / canvasRect.height);
    }
    const text = this.textValue.trim();
    this.textEditing.set(null);
    this.textValue = '';
    if (!text || !this.ctx) {
      return;
    }
    this.checkpoint(this.ctx.getImageData(0, 0, canvas.width, canvas.height));
    this.ctx.font = `700 ${this.textCanvasPx()}px Montserrat, sans-serif`;
    this.ctx.fillStyle = this.color();
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(text, drawX, drawY);
  }

  cancelText(): void {
    this.textEditing.set(null);
    this.textValue = '';
  }

  /** Guarda el estado actual como punto de deshacer y anula lo rehacible. */
  private checkpoint(snapshot: ImageData): void {
    this.undoStack.push(snapshot);
    if (this.undoStack.length > ImagePaintDialogComponent.MAX_UNDO) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.updateDepths();
  }

  private updateDepths(): void {
    this.undoDepth.set(this.undoStack.length);
    this.redoDepth.set(this.redoStack.length);
  }

  undo(): void {
    const previous = this.undoStack.pop();
    if (previous && this.ctx) {
      const canvas = this.canvasRef().nativeElement;
      this.redoStack.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height));
      this.ctx.putImageData(previous, 0, 0);
    }
    this.updateDepths();
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (next && this.ctx) {
      const canvas = this.canvasRef().nativeElement;
      this.undoStack.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height));
      this.ctx.putImageData(next, 0, 0);
    }
    this.updateDepths();
  }

  /** Atajos: Cmd/Ctrl+Z deshace; Cmd/Ctrl+Shift+Z o Cmd/Ctrl+Y rehace. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.textEditing()) {
      return; // el input de texto usa el deshacer nativo del navegador
    }
    if (!event.metaKey && !event.ctrlKey) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.redo();
    } else if (key === 'z') {
      event.preventDefault();
      this.undo();
    } else if (key === 'y') {
      event.preventDefault();
      this.redo();
    }
  }

  save(): void {
    this.canvasRef().nativeElement.toBlob(blob => {
      this.dialogRef.close(blob ?? undefined);
    }, 'image/png');
  }
}
